/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import { Col, Row } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import { BrickLoader } from "@nordcloud/gnui";
import {
  useEventStatusChangedSubscription,
  EventStatus,
} from "~/generated/graphql";
import { ErrorWrap, BreadcrumbsBox, LoaderWrap } from "~/components";
import { NoData } from "~/components/NoData";
import { ROUTES } from "~/routing/routes";
import { IncomingEventDetails } from "~/views/events/EventsDetails/IncomingEventDetails";
import { EventDetailsParams } from "~/views/events/EventsDetails/types";
import { ApproveEventMessage } from "../components";
import { useGetEventView } from "../hooks";
import { EventInfo } from "./components";
import { EventCtxProvider } from "./EventProvider";
import { EventDetailsTab } from "./Tabs/Details/EventDetailsTab";
import { Tabs } from "./Tabs/Tabs";

export function EventDetails() {
  const { eventId } = useParams<EventDetailsParams>();
  const { event, loading, refetch, error } = useGetEventView({
    eventId,
  });

  const { data } = useEventStatusChangedSubscription({
    variables: {
      input: {
        id: eventId,
      },
    },
  });

  useEffect(() => {
    if (
      data?.eventStatusChanged &&
      data.eventStatusChanged !== EventStatus.InProgress
    ) {
      refetch();
    }
  }, [data?.eventStatusChanged, refetch]);

  if (loading) {
    return <BrickLoader height="14rem" />;
  }

  if (error?.toString().includes("Resource not found")) {
    return <IncomingEventDetails eventId={eventId} />;
  }

  const title = event?.name ?? eventId;

  const eventUpdate = {
    ...event,
    status: data?.eventStatusChanged ?? event?.status,
  };

  const breadcrumbs = [
    { label: "Home", uri: "/" },
    { label: "Events", uri: ROUTES.events.index },
    { label: title, uri: "" },
  ];

  return (
    <ErrorWrap
      error={error}
      Component={() => <NoData hasIcon message={error?.message} />}
    >
      <EventCtxProvider value={{ event: eventUpdate }}>
        <LoaderWrap
          Component={() => <BrickLoader height="14rem" />}
          loading={loading}
          viewBox="0 0 10 4"
        >
          <Row>
            <Col>
              <BreadcrumbsBox title={title} customBreadcrumbs={breadcrumbs} />
            </Col>
          </Row>
          <ApproveEventMessage />
          <Row>
            <Col>
              <EventInfo />
            </Col>
          </Row>
          <Row>
            <Col>
              <Tabs detailsTab={<EventDetailsTab />} />
            </Col>
          </Row>
        </LoaderWrap>
      </EventCtxProvider>
    </ErrorWrap>
  );
}
